/**
 * Keepsa nudges — honest, real-date urgency.
 *
 * All dates are computed in IST (UTC+5:30) in the browser, so cached pages never show a stale day.
 * Business days skip Sundays, matching the "dispatched in 2 business days" promise.
 *
 *  [data-kp-eta]                 container with data-dispatch / data-min / data-max (business days)
 *    [data-kp-countdown]         → time left to order today ("3h 12m")
 *    [data-kp-date=dispatch|min|max] → real dates
 *  [data-kp-occasion]            hidden until an occasion deadline applies; filled from window.KeepsaOccasion
 *    [data-kp-occ-name] [data-kp-occ-date] [data-kp-occ-left]
 */
(() => {
  if (window.__kpNudges) return;
  window.__kpNudges = true;

  const IST_OFFSET = 330 * 60 * 1000;
  const DAY = 86400000;
  const fmt = new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });

  /** "Now" as a UTC-based Date whose UTC fields read as IST wall-clock time. */
  const nowIST = () => new Date(Date.now() + IST_OFFSET);
  const dayStart = (d) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const todayIST = () => dayStart(nowIST());

  const addBusinessDays = (date, n) => {
    let d = new Date(date);
    let counted = 0;
    while (counted < n) {
      d = new Date(d.getTime() + DAY);
      if (d.getUTCDay() !== 0) counted += 1; // Sunday = 0
    }
    return d;
  };
  const label = (d) => fmt.format(d).replace(/,/g, ',');

  // ---------- Delivery estimate + order-today countdown ----------
  const updateEta = () => {
    const today = todayIST();
    document.querySelectorAll('[data-kp-eta]').forEach((el) => {
      const dispatch = addBusinessDays(today, Number(el.dataset.dispatch || 2));
      // Same convention as the server-rendered estimate: delivery window counted from the order day.
      const min = addBusinessDays(today, Number(el.dataset.min || 5));
      const max = addBusinessDays(today, Number(el.dataset.max || 7));
      const set = (key, d) => el.querySelectorAll(`[data-kp-date="${key}"]`).forEach((n) => (n.textContent = label(d)));
      set('dispatch', dispatch);
      set('min', min);
      set('max', max);
    });

    const msLeft = today.getTime() + DAY - nowIST().getTime();
    const h = Math.floor(msLeft / 3600000);
    const m = Math.max(1, Math.floor((msLeft % 3600000) / 60000));
    const text = h > 0 ? `${h}h ${m}m` : `${m} min`;
    document.querySelectorAll('[data-kp-countdown]').forEach((n) => (n.textContent = text));
  };

  // ---------- Occasion deadline (e.g. Diwali) ----------
  /** Last day an order can be placed and still arrive (worst case) the day before the occasion. */
  const occasionDeadline = (cfg) => {
    const occ = new Date(`${cfg.date}T00:00:00Z`);
    if (Number.isNaN(occ.getTime())) return null;
    const arriveBy = new Date(occ.getTime() - DAY);
    let last = null;
    for (let d = todayIST(); d < occ; d = new Date(d.getTime() + DAY)) {
      const arrives = addBusinessDays(addBusinessDays(d, cfg.dispatch), cfg.max);
      if (arrives <= arriveBy) last = d;
      else break;
    }
    return last;
  };

  const updateOccasion = () => {
    const cfg = window.KeepsaOccasion;
    const nodes = document.querySelectorAll('[data-kp-occasion]');
    if (!cfg || !cfg.name || !cfg.date) {
      nodes.forEach((n) => (n.hidden = true));
      return;
    }
    const last = occasionDeadline(cfg);
    const today = todayIST();
    const daysLeft = last ? Math.round((last - today) / DAY) : -1;
    const show = last && daysLeft >= 0 && daysLeft <= (cfg.window || 21);
    nodes.forEach((n) => {
      n.hidden = !show;
      if (!show) return;
      n.querySelectorAll('[data-kp-occ-name]').forEach((x) => (x.textContent = cfg.name));
      n.querySelectorAll('[data-kp-occ-date]').forEach((x) => (x.textContent = label(last)));
      n.querySelectorAll('[data-kp-occ-left]').forEach((x) => {
        x.textContent = daysLeft === 0 ? 'last day today' : daysLeft === 1 ? '1 day left' : `${daysLeft} days left`;
      });
      n.classList.toggle('is-last-day', daysLeft === 0);
    });
  };

  const run = () => {
    updateEta();
    updateOccasion();
  };

  // Cart drawer re-renders on every change; re-fill its slots when new markup lands.
  const observer = new MutationObserver((records) => {
    for (const r of records) {
      for (const node of r.addedNodes) {
        if (node.nodeType === 1 && (node.matches?.('[data-kp-eta],[data-kp-occasion]') || node.querySelector?.('[data-kp-eta],[data-kp-occasion]'))) {
          run();
          return;
        }
      }
    }
  });

  const start = () => {
    run();
    observer.observe(document.body, { childList: true, subtree: true });
    setInterval(updateEta, 30000);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.KeepsaNudges = { run, occasionDeadline, addBusinessDays, todayIST };
})();
